import type { CodeableConcept } from "@medplum/fhirtypes";
import { CommandGroup, CommandItem } from "@repo/ui/components/ui/command";
import { useIcdCodeSearch } from "@/shared/hooks/use-icd-code-search";
import { SearchPicker } from "@/shared/pickers/search-picker";

interface IcdCodePickerProps {
  value?: CodeableConcept;
  onChange: (value: CodeableConcept | undefined) => void;
  placeholder?: string;
}

export function IcdCodePicker({ value, onChange, placeholder = "Search ICD-10 codes..." }: IcdCodePickerProps) {
  const displayText = value?.coding?.[0] ? `${value.coding[0].code} — ${value.coding[0].display}` : undefined;
  return (
    <SearchPicker.Root placeholder={placeholder}>
      <SearchPicker.Trigger
        label={displayText}
        onClear={value ? () => onChange(undefined) : undefined}
        clearLabel="Clear ICD-10 code"
      />
      <SearchPicker.Content>
        <IcdResults value={value} onChange={onChange} />
      </SearchPicker.Content>
    </SearchPicker.Root>
  );
}

function IcdResults({ value, onChange }: IcdCodePickerProps) {
  const { state, actions } = SearchPicker.useContext();
  const { options, isLoading } = useIcdCodeSearch(state.query);
  return (
    <SearchPicker.Search>
      <SearchPicker.Results isLoading={isLoading} count={options.length} emptyMessage="No codes found.">
        <CommandGroup>
          {options.map((option) => (
            <CommandItem
              key={option.code}
              value={option.code}
              onSelect={() => {
                onChange({ coding: [option.coding], text: option.display });
                actions.close();
              }}
              data-checked={value?.coding?.[0]?.code === option.code ? "true" : undefined}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">{option.code}</span>
                <span className="text-xs text-muted-foreground">{option.display}</span>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </SearchPicker.Results>
    </SearchPicker.Search>
  );
}
